import { useEffect, useState } from 'react'
import { newsAPI } from '../services/api'
import type { NewsHeadline } from '../types'
import { parseApiDate } from '../utils/signalExpiry'

/**
 * The switch: build with REACT_APP_NEWS_TICKER=off (or set this to false) and
 * the Dashboard renders no news strip and fetches no headlines. To remove the
 * ticker entirely, delete its one line at the bottom of pages/Dashboard.tsx.
 */
export const NEWS_TICKER_ENABLED = process.env.REACT_APP_NEWS_TICKER !== 'off'

// The backend's fetch task stores new headlines every 30 minutes; polling
// faster would only re-read the same rows.
export const NEWS_POLL_INTERVAL_MS = 30 * 60_000

const HEADLINE_LIMIT = 30

// Scroll time per character of text, so the strip moves at a steady, easy
// reading pace (~40px/s) however many headlines it carries. Slower than the
// price tape: a headline takes longer to read than a price. Tied to the font
// size below: larger type is wider per character.
const SECONDS_PER_CHAR = 0.2

/** "12m", "3h", "2d": how long ago the outlet published it. */
export const headlineAge = (publishedAt: string, now: number): string => {
  const minutes = Math.max(0, Math.floor((now - parseApiDate(publishedAt).getTime()) / 60_000))
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`
}

const HeadlineItem = ({ headline, now, duplicate }: { headline: NewsHeadline; now: number; duplicate?: boolean }) => (
  <li className="flex items-center">
    <a
      href={headline.url}
      target="_blank"
      rel="noopener noreferrer"
      // The looping copy is decoration: out of the tab order and screen readers.
      tabIndex={duplicate ? -1 : undefined}
      className="group inline-flex items-baseline gap-2.5 whitespace-nowrap px-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      data-testid={duplicate ? undefined : 'news-headline'}
    >
      <span className="text-xs font-semibold uppercase tracking-wide text-primary-300">{headline.source}</span>
      <span className="text-gray-200 group-hover:text-white group-hover:underline">{headline.title}</span>
      <span className="font-mono text-xs tabular-nums text-gray-500">
        <span className="sr-only">published </span>
        {headlineAge(headline.published_at, now)}
        <span className="sr-only"> ago</span>
      </span>
    </a>
    <span aria-hidden="true" className="text-gray-600">
      ·
    </span>
  </li>
)

/**
 * A slow crawl of the latest crypto headlines along the bottom of the
 * Dashboard. Every item is a real feed entry from CoinDesk, Cointelegraph or
 * Bitcoin Magazine: the outlet's own headline, credited to it, linking to the
 * article on its site. Nothing is summarised or written by us.
 *
 * Pauses on hover and keyboard focus; with reduced motion it stops and the
 * strip scrolls by hand instead. Renders nothing until there are headlines.
 */
const NewsTicker = () => {
  const [headlines, setHeadlines] = useState<NewsHeadline[]>([])
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const rows = await newsAPI.getHeadlines(HEADLINE_LIMIT)
        if (!active) return
        // An empty answer keeps what is on screen: the feeds may just be slow.
        if (rows.length) setHeadlines(rows)
        setNow(Date.now())
      } catch (error) {
        console.error('News ticker refresh failed:', error)
      }
    }
    load()
    const timer = setInterval(load, NEWS_POLL_INTERVAL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  if (headlines.length === 0) return null

  const chars = headlines.reduce((sum, h) => sum + h.title.length + h.source.length + 8, 0)

  return (
    <aside
      aria-label="Latest crypto news headlines"
      // Bleeds past the terminal's side and bottom padding and sticks to the
      // bottom of the viewport, mirroring the price tape pinned to the top.
      // The bottom padding clears a phone's home indicator.
      className="sticky bottom-0 z-20 -mx-4 -mb-6 mt-8 flex items-stretch border-t border-terminal-border bg-[#0a0e12]/95 pb-[env(safe-area-inset-bottom)] text-[15px] leading-6 shadow-[0_-4px_12px_rgba(0,0,0,0.35)] backdrop-blur md:-mx-8"
      data-testid="news-ticker"
    >
      <div className="flex shrink-0 items-center gap-2 border-r border-terminal-border px-4 text-xs font-semibold uppercase tracking-wider text-gray-300">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-red-500" />
        News
      </div>
      <div
        // Stops short of the assistant button fixed to the bottom-right corner
        // (right-4, w-16: it covers 16-80px from the edge), with a 16px gap,
        // so headlines never run underneath it.
        className="ticker-tape mr-24 min-w-0 flex-1 overflow-hidden py-3"
        style={{ ['--ticker-duration' as string]: `${Math.round(chars * SECONDS_PER_CHAR)}s` }}
      >
        <div className="ticker-track">
          <ul className="flex items-center">
            {headlines.map((headline) => (
              <HeadlineItem key={headline.url} headline={headline} now={now} />
            ))}
          </ul>
          <ul className="flex items-center" aria-hidden="true">
            {headlines.map((headline) => (
              <HeadlineItem key={`${headline.url}-duplicate`} headline={headline} now={now} duplicate />
            ))}
          </ul>
        </div>
      </div>
    </aside>
  )
}

export default NewsTicker
